import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Upload } from 'lucide-react'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { UsageWarningBanner } from '@/components/common/UsageInfo'
import WarehouseLookupBulkImportDialog from '@/components/WarehouseLookups/WarehouseLookupBulkImportDialog'
import WarehouseLookupEntriesGrid from '@/components/WarehouseLookups/WarehouseLookupEntriesGrid'
import WarehouseLookupEntryDialog from '@/components/WarehouseLookups/WarehouseLookupEntryDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getWarehouseLookupEntryUsage } from '@/api/adminWarehouseLookups'
import { useDebounce } from '@/hooks/useDebounce'
import {
  useAdminWarehouseLookupEntries,
  useAdminWarehouseLookupTables,
  useBulkUpsertAdminWarehouseLookupEntries,
  useCreateAdminWarehouseLookupEntry,
  useDeleteAdminWarehouseLookupEntry,
  useUpdateAdminWarehouseLookupEntry,
} from '@/hooks/useAdminWarehouseLookups'
import { toast } from '@/stores/useToastStore'
import type {
  WarehouseLookupEntry,
  WarehouseLookupEntryUsage,
  WarehouseLookupTableName,
} from '@/types/adminWarehouseLookups'

const DEFAULT_PAGE_SIZE = 20

interface UsageEntry {
  data?: WarehouseLookupEntryUsage
  isLoading: boolean
}

export default function AdminWarehouseLookups() {
  const [activeTable, setActiveTable] =
    useState<WarehouseLookupTableName | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.ADMIN_WAREHOUSE_LOOKUP_ENTRIES,
    DEFAULT_PAGE_SIZE
  )

  const [createOpen, setCreateOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [selectedEntry, setSelectedEntry] =
    useState<WarehouseLookupEntry | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WarehouseLookupEntry | null>(
    null
  )

  const [usageMap, setUsageMap] = useState<Record<string, UsageEntry>>({})
  const usageMapRef = useRef(usageMap)
  usageMapRef.current = usageMap

  const debouncedSearch = useDebounce(searchInput, 300)

  const tablesQuery = useAdminWarehouseLookupTables()
  const tableSummaries = useMemo(
    () => tablesQuery.data ?? [],
    [tablesQuery.data]
  )

  useEffect(() => {
    if (!activeTable && tableSummaries.length > 0) {
      setActiveTable(tableSummaries[0].table_name)
    }
  }, [activeTable, tableSummaries])

  useEffect(() => {
    setPage(1)
  }, [activeTable, debouncedSearch, pageSize])

  const entriesQuery = useAdminWarehouseLookupEntries({
    tableName: activeTable,
    params: {
      search: debouncedSearch || undefined,
      skip: (page - 1) * pageSize,
      limit: pageSize,
    },
  })

  const entries = entriesQuery.data?.data ?? []
  const total = entriesQuery.data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const createMutation = useCreateAdminWarehouseLookupEntry()
  const updateMutation = useUpdateAdminWarehouseLookupEntry()
  const deleteMutation = useDeleteAdminWarehouseLookupEntry()
  const bulkMutation = useBulkUpsertAdminWarehouseLookupEntries()

  useEffect(() => {
    if (tablesQuery.isError) {
      toast.error('Failed to load lookup tables')
    }
  }, [tablesQuery.isError])

  useEffect(() => {
    if (entriesQuery.isError) {
      toast.error('Failed to load lookup entries')
    }
  }, [entriesQuery.isError])

  const activeTableLabel = useMemo(() => {
    if (!activeTable) return ''
    return activeTable.replace(/_/g, ' ')
  }, [activeTable])

  const fetchUsage = useCallback(
    async (entry: WarehouseLookupEntry) => {
      const key = entry.lookup_key
      const current = usageMapRef.current[key]
      if (!activeTable || current?.data || current?.isLoading) return

      setUsageMap(prev => ({ ...prev, [key]: { isLoading: true } }))
      try {
        const data = await getWarehouseLookupEntryUsage(
          activeTable,
          entry.lookup_key
        )
        setUsageMap(prev => ({ ...prev, [key]: { data, isLoading: false } }))
      } catch {
        setUsageMap(prev => ({ ...prev, [key]: { isLoading: false } }))
      }
    },
    [activeTable]
  )

  const handleEdit = (entry: WarehouseLookupEntry) => {
    setSelectedEntry(entry)
    setEditOpen(true)
    fetchUsage(entry)
  }

  const handleDelete = (entry: WarehouseLookupEntry) => {
    setDeleteTarget(entry)
    setDeleteOpen(true)
    fetchUsage(entry)
  }

  const handleCreateSubmit = (payload: {
    lookup_key: string
    value_text: string | null
    value_num: number | null
  }) => {
    if (!activeTable) return

    createMutation.mutate(
      { tableName: activeTable, payload },
      { onSuccess: () => setCreateOpen(false) }
    )
  }

  const handleEditSubmit = (payload: {
    lookup_key: string
    value_text: string | null
    value_num: number | null
    is_active?: boolean
  }) => {
    if (!activeTable || !selectedEntry) return

    updateMutation.mutate(
      { tableName: activeTable, id: selectedEntry.id, payload },
      {
        onSuccess: () => {
          setEditOpen(false)
          setSelectedEntry(null)
          setUsageMap({})
        },
      }
    )
  }

  const handleDeleteConfirm = () => {
    if (!activeTable || !deleteTarget) return

    deleteMutation.mutate(
      { tableName: activeTable, id: deleteTarget.id },
      {
        onSuccess: () => {
          setDeleteOpen(false)
          setDeleteTarget(null)
          setUsageMap({})
        },
      }
    )
  }

  const handleBulkSubmit = (
    payload: {
      lookup_key: string
      value_text?: string | null
      value_num?: number | null
    }[]
  ) => {
    if (!activeTable) return

    bulkMutation.mutate(
      { tableName: activeTable, payload: { entries: payload } },
      {
        onSuccess: () => {
          setBulkOpen(false)
          setUsageMap({})
        },
      }
    )
  }

  const deleteUsage = deleteTarget
    ? usageMap[deleteTarget.lookup_key]
    : undefined
  const editUsage = selectedEntry
    ? usageMap[selectedEntry.lookup_key]
    : undefined

  return (
    <>
      <WarehouseLookupEntryDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        isPending={createMutation.isPending}
        onSubmit={handleCreateSubmit}
      />

      <WarehouseLookupEntryDialog
        open={editOpen}
        onOpenChange={nextOpen => {
          setEditOpen(nextOpen)
          if (!nextOpen) setSelectedEntry(null)
        }}
        mode="edit"
        entry={selectedEntry}
        isPending={updateMutation.isPending}
        onSubmit={handleEditSubmit}
        usageBanner={
          <UsageWarningBanner
            usage={editUsage?.data}
            isLoading={editUsage?.isLoading ?? false}
          />
        }
      />

      <WarehouseLookupBulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        isPending={bulkMutation.isPending}
        onSubmit={handleBulkSubmit}
      />

      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Deactivate Lookup Entry"
        description={`Are you sure you want to deactivate "${deleteTarget?.lookup_key ?? ''}"?`}
        confirmText="Deactivate"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      >
        <UsageWarningBanner
          usage={deleteUsage?.data}
          isLoading={deleteUsage?.isLoading ?? false}
        />
      </ConfirmationDialog>

      <div className="flex flex-col gap-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Lookups</h1>
          <p className="mt-1 text-base text-muted-foreground">
            Manage lookup tables and their entries.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[280px,1fr]">
          <div className="rounded-lg border border-border bg-card p-3">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              Tables
            </h2>
            {tablesQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading tables...</p>
            ) : tableSummaries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No lookup tables found.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {tableSummaries.map(table => {
                  const isActive = table.table_name === activeTable

                  return (
                    <button
                      key={table.table_name}
                      type="button"
                      onClick={() => {
                        setActiveTable(table.table_name)
                        setUsageMap({})
                      }}
                      className={`cursor-pointer rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium capitalize">
                          {table.table_name.replace(/_/g, ' ')}
                        </span>
                        <Badge variant={isActive ? 'info' : 'neutral'}>
                          {table.entry_count}
                        </Badge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {table.active_count} active
                      </p>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h3 className="text-base font-semibold capitalize text-foreground">
                    {activeTableLabel || 'Select a table'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Showing {entries.length} of {total} entries
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setBulkOpen(true)}
                  >
                    <Upload className="h-4 w-4" />
                    Bulk Import
                  </Button>
                  <Button type="button" onClick={() => setCreateOpen(true)}>
                    <Plus className="h-4 w-4" />
                    Add Entry
                  </Button>
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <Input
                  placeholder="Search by lookup key..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="sm:max-w-sm"
                />
              </div>
            </div>

            <WarehouseLookupEntriesGrid
              entries={entries}
              isLoading={entriesQuery.isLoading || !activeTable}
              usageMap={usageMap}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onFetchUsage={fetchUsage}
            />

            <div className="rounded-lg border border-border bg-card">
              <Pagination
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
